import { useEffect, useRef, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { CommentActionModal } from './CommentComposer';
import { getUserOrganizationId, isSmartLightEmployee } from '../../authUtils';
import { fetchStatusReasonOptions, postActivity } from '../../services/referralService';

const EMPTY_VALUE = '—';

const EMPTY_OPTIONS = {
  is_hcsc: false,
  statuses: [],
  reasons_by_status: {},
  status_id_by_reason_id: {},
};

const CommentModal = ({
  open,
  onClose,
  onSuccess,
  refId,
  // Legacy TPA id of the referral's external party. SL employees use it to
  // fetch the carrier's reason hierarchy (instead of SL's own).
  referralTpaId = null,
  // The leaf reason_code (UUID) on the latest status-bearing activity in the
  // drawer's thread, or null for brand-new referrals.
  latestStatusReasonId = null,
  currentStatus,
  currentReason,
}) => {
  const [comment, setComment] = useState('');
  const [showExternal, setShowExternal] = useState(false);
  const [needsReply, setNeedsReply] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [reasonUpdate, setReasonUpdate] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [reasonsState, setReasonsState] = useState('idle'); // 'idle' | 'loading' | 'loaded' | 'error'
  const textareaRef = useRef(null);
  const successTimerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setComment('');
    setShowExternal(false);
    setNeedsReply(false);
    setStatusUpdate('');
    setReasonUpdate('');
    setStatus('idle');
    setErrorMessage('');
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (open && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [open]);

  const isSL = isSmartLightEmployee();
  const needsReplyLabel = isSL ? 'Request response' : 'Request response from SmartLight';

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setReasonsState('loading');
    setOptions(EMPTY_OPTIONS);
    // SL employees see whichever carrier owns the referral; carrier users see
    // their own org (the legacy path).
    // eslint-disable-next-line eqeqeq -- intentional null/undefined nullish check
    const fetchPromise = isSL && referralTpaId != null
      ? fetchStatusReasonOptions(null, { legacyTpaId: referralTpaId })
      : fetchStatusReasonOptions(getUserOrganizationId());
    fetchPromise
      .then((data) => {
        if (cancelled) return;
        setOptions({ ...EMPTY_OPTIONS, ...(data ?? {}) });
        setReasonsState('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setReasonsState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [open, isSL, referralTpaId]);
  const trimmedLength = comment.trim().length;
  const showChanged = statusUpdate !== '' || reasonUpdate !== '';

  const statusOptions = options.statuses;
  // The Status that the latest activity's leaf reason rolls up to (its parent,
  // or the leaf itself for the migrate_comments universal fallback). The
  // backend supplies the leaf→status mapping so the Reason dropdown can
  // pre-filter to the current Status's siblings without the user re-picking.
  const effectiveStatusReasonId = latestStatusReasonId
    ? options.status_id_by_reason_id[latestStatusReasonId] ?? null
    : null;
  const activeStatusForReasonFilter = statusUpdate || effectiveStatusReasonId;
  const reasonOptions = activeStatusForReasonFilter
    ? options.reasons_by_status[activeStatusForReasonFilter] ?? []
    : [];

  const dropdownsDisabled = reasonsState !== 'loaded';
  const reasonRequiresStatusFirst =
    !activeStatusForReasonFilter && reasonsState === 'loaded';
  const reasonMissing = statusUpdate !== '' && reasonUpdate === '';
  // Newly-published referrals (no prior status-bearing activity) must have a
  // Status AND Reason picked before submit.
  const isNewlyPublished = !latestStatusReasonId;
  const statusReasonRequiredButMissing =
    isNewlyPublished && (statusUpdate === '' || reasonUpdate === '');
  // Comment typed before Status/Reason on a newly-published referral —
  // escalate from a gray hint to a red prompt.
  const promptForStatusReasonOnNew =
    statusReasonRequiredButMissing && trimmedLength > 0;
  const submitDisabled =
    trimmedLength < 1 ||
    status === 'submitting' ||
    reasonMissing ||
    statusReasonRequiredButMissing;

  const handleCommentChange = (e) => {
    setComment(e.target.value);
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleStatusChange = (e) => {
    setStatusUpdate(e.target.value);
    // Picking a new Status invalidates any previously-chosen Reason — the
    // available reasons are filtered by the chosen Status.
    setReasonUpdate('');
  };

  const handleSubmit = async () => {
    const payload = {
      comment,
      show_external: isSL ? showExternal : true,
      needs_reply: needsReply,
    };
    if (reasonUpdate) {
      payload.reason_code = reasonUpdate;
    }
    setStatus('submitting');
    setErrorMessage('');
    try {
      await postActivity(refId, payload);
      if (onSuccess) onSuccess();
      setStatus('success');
      successTimerRef.current = setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err?.message || 'Failed to post comment. Try again.');
    }
  };

  return (
    <CommentActionModal
      open={open}
      onClose={onClose}
      title="Comment & Update Status"
      contentTestId="comment-modal"
      comment={comment}
      onCommentChange={handleCommentChange}
      textareaRef={textareaRef}
      showExternal={showExternal}
      onShowExternalChange={setShowExternal}
      showExternalVisible={isSL}
      needsReply={needsReply}
      onNeedsReplyChange={setNeedsReply}
      needsReplyLabel={needsReplyLabel}
      showPendingBadge
      status={status}
      errorMessage={errorMessage}
      onSubmit={handleSubmit}
      submitDisabled={submitDisabled}
      successMessage="Comment posted."
    >
      <section>
        <div className="flex gap-2 items-center">
          <ClipboardList className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="text-sm font-bold tracking-tight text-foreground">Status</span>
          {showChanged && (
            <span
              data-testid="changed-badge"
              className="inline-flex items-center h-4 px-1.5 py-0 text-[10px] font-medium bg-primary/10 text-primary border border-primary/20 rounded-full"
            >
              Changed
            </span>
          )}
        </div>
        <div
          data-testid="status-card"
          className="mt-2 border border-border bg-muted/30 p-4 rounded-lg space-y-3"
        >
          <div className="grid grid-cols-[max-content_minmax(0,1fr)_max-content_minmax(0,1fr)] gap-x-3 gap-y-3 items-center">
            <span className="text-xs font-semibold whitespace-nowrap text-foreground">Current Status</span>
            <span className="text-xs text-foreground break-words">{currentStatus || EMPTY_VALUE}</span>
            <span className="text-xs font-semibold whitespace-nowrap text-foreground">Current Reason</span>
            <span className="text-xs text-foreground break-words">{currentReason || EMPTY_VALUE}</span>

            <label
              htmlFor="status-update-select"
              className="text-xs font-semibold whitespace-nowrap text-foreground"
            >
              Update Status to
            </label>
            <select
              id="status-update-select"
              data-testid="status-update-select"
              value={statusUpdate}
              onChange={handleStatusChange}
              disabled={dropdownsDisabled}
              className="h-7 w-full max-w-[180px] text-xs rounded-md border border-input bg-background px-3 text-foreground focus:outline-none focus:border-ring disabled:opacity-60 disabled:pointer-events-none"
            >
              <option value="">
                {reasonsState === 'loading' ? 'Loading…' : 'No change'}
              </option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label
              htmlFor="reason-update-select"
              className="text-xs font-semibold whitespace-nowrap text-foreground"
            >
              Update Reason to
            </label>
            <select
              id="reason-update-select"
              data-testid="reason-update-select"
              value={reasonUpdate}
              onChange={(e) => setReasonUpdate(e.target.value)}
              disabled={dropdownsDisabled || reasonRequiresStatusFirst}
              className="h-7 w-full text-xs rounded-md border border-input bg-background px-3 text-foreground focus:outline-none focus:border-ring disabled:opacity-60 disabled:pointer-events-none"
            >
              <option value="">
                {reasonsState === 'loading' ? 'Loading…' : 'No change'}
              </option>
              {reasonOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {/* Fixed-height slot for inline helper text so toggling
              messages doesn't resize the status card or modal. */}
          <div className="min-h-[1rem]">
            {reasonsState === 'error' && (
              <p
                data-testid="reasons-load-error"
                className="text-xs text-destructive"
              >
                Failed to load statuses and reasons.
              </p>
            )}
            {promptForStatusReasonOnNew && (
              <p
                data-testid="status-reason-required-on-new"
                className="text-xs text-destructive text-center"
              >
                Please update Status and Reason
              </p>
            )}
            {!promptForStatusReasonOnNew && reasonRequiresStatusFirst && (
              <p data-testid="reason-needs-status" className="text-xs text-muted-foreground text-center">
                Select a Status first.
              </p>
            )}
            {!isNewlyPublished && reasonMissing && (
              <p data-testid="reason-required" className="text-xs text-destructive">
                Pick a reason or set Status back to No change.
              </p>
            )}
          </div>
        </div>
      </section>
    </CommentActionModal>
  );
};

export { CommentModal };
